// Recolorable GAA jersey SVG. Drawn in a 0..100 x 0..100 box so it can be
// dropped into any SVG (nested <svg>) or rendered standalone.

export const JERSEY_STYLES = [
  { id: "plain", name: "Plain" },
  { id: "sleeves", name: "Contrast sleeves" },
  { id: "hoop", name: "Chest hoop" },
  { id: "twoHoops", name: "Two hoops" },
  { id: "hooped", name: "Hooped" },
  { id: "stripes", name: "Vertical stripes" },
  { id: "sash", name: "Sash" },
  { id: "halves", name: "Halves" },
  { id: "yoke", name: "Shoulder yoke" },
  { id: "cross", name: "Cross" },
  { id: "chest", name: "Chest band" },
  { id: "bandLow", name: "Waist band" },
];

const OUTLINE = "M34,24 L40,24 C44,29 56,29 60,24 L66,24 L86,34 L80,48 L68,42 L70,90 L30,90 L32,42 L20,48 L14,34 Z";
const SLEEVE_L = "M34,24 L14,34 L20,48 L32,42 Z";
const SLEEVE_R = "M66,24 L86,34 L80,48 L68,42 Z";

function lum(hex) {
  const h = (hex || "#888").replace("#", "");
  const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  const r = parseInt(f.slice(0, 2), 16), g = parseInt(f.slice(2, 4), 16), b = parseInt(f.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
export const contrastText = (hex) => (lum(hex) > 0.58 ? "#0B1220" : "#FFFFFF");

export function autoAway(homeBody) {
  // pick a clearly contrasting away kit
  const l = lum(homeBody);
  const body = l > 0.55 ? "#B91C1C" : l < 0.25 ? "#F1F5F9" : "#0F172A";
  return { style: "plain", body, sleeves: body, trim: "#FFFFFF", accent: "#E2E8F0" };
}
export function gkColors(team) {
  const body = team === "A" ? "#FACC15" : "#F97316";
  return { style: "plain", body, sleeves: body, trim: "#111827", accent: "#111827" };
}
export function defaultJersey(primary = "#0EA5E9") {
  return { style: "plain", body: primary, sleeves: primary, trim: "#FFFFFF", accent: "#FFFFFF" };
}

function Pattern({ style, accent, clip }) {
  const c = { clipPath: `url(#${clip})` };
  switch (style) {
    case "hoop": return <rect x="0" y="44" width="100" height="13" fill={accent} style={c} />;
    case "chest": return <rect x="0" y="43" width="100" height="7" fill={accent} style={c} />;
    case "bandLow": return <rect x="0" y="64" width="100" height="11" fill={accent} style={c} />;
    case "twoHoops": return <g style={c}><rect x="0" y="40" width="100" height="6" fill={accent} /><rect x="0" y="52" width="100" height="6" fill={accent} /></g>;
    case "hooped": return <g style={c}>{[34, 46, 58, 70, 82].map((y) => <rect key={y} x="0" y={y} width="100" height="5" fill={accent} />)}</g>;
    case "stripes": return <g style={c}>{[35, 46, 57, 68].map((x) => <rect key={x} x={x} width="5" y="0" height="100" fill={accent} />)}</g>;
    case "sash": return <g style={c}><rect x="-20" y="42" width="150" height="13" fill={accent} transform="rotate(-26 50 50)" /></g>;
    case "halves": return <rect x="50" y="0" width="50" height="100" fill={accent} style={c} />;
    case "yoke": return <rect x="0" y="18" width="100" height="18" fill={accent} style={c} />;
    case "cross": return <g style={c}><rect x="45" y="20" width="10" height="70" fill={accent} /><rect x="10" y="45" width="80" height="10" fill={accent} /></g>;
    default: return null;
  }
}

// Renders jersey parts into the current SVG coordinate space (expects a 0..100 box).
export function JerseyShape({ style = "plain", body = "#0EA5E9", sleeves, trim = "#FFFFFF", accent = "#FFFFFF", number, showNumber = true, uid }) {
  const clip = `jclip-${uid || Math.random().toString(36).slice(2, 7)}`;
  const slv = style === "sleeves" ? accent : (sleeves || body);
  const numColor = contrastText(body);
  return (
    <g>
      <defs><clipPath id={clip}><path d={OUTLINE} /></clipPath></defs>
      <path d={OUTLINE} fill={body} />
      <Pattern style={style} accent={accent} clip={clip} />
      <path d={SLEEVE_L} fill={slv} />
      <path d={SLEEVE_R} fill={slv} />
      <path d="M40,24 C44,29 56,29 60,24" fill="none" stroke={trim} strokeWidth="3.2" strokeLinecap="round" />
      <path d={OUTLINE} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="1.6" strokeLinejoin="round" />
      {showNumber && number != null && (
        <text x="50" y="66" textAnchor="middle" fontSize="30" fontWeight="900"
          fill={numColor} stroke={numColor === "#FFFFFF" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.6)"} strokeWidth="1.4" paintOrder="stroke"
          style={{ fontFamily: "Outfit, sans-serif" }}>{number}</text>
      )}
    </g>
  );
}

// Standalone jersey for pickers/thumbnails.
export default function Jersey({ colors = {}, number, size = 48, className, showNumber = true }) {
  const c = { ...defaultJersey(), ...colors };
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className}>
      <JerseyShape {...c} number={number} showNumber={showNumber} />
    </svg>
  );
}
